import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

const categories = [
  { id: "product", label: "Product" },
  { id: "process", label: "Process" },
  { id: "experience", label: "Experience" },
  { id: "privacy", label: "Privacy" },
  { id: "outcomes", label: "Outcomes" },
];

const faqData = {
  product: [
    {
      q: "Isn't this just ChatGPT?",
      a: [
        "Not even close. ChatGPT is a general purpose tool. It knows nothing about you, remembers nothing about you, and has no stake in your outcome. It will help you write a cover letter and then help the next person write a ransom note. It has no mission, no clinical framework, and no idea where you are in your life.",
        "Become is a purpose-built transformation platform. Nia, your guide, builds a complete picture of who you are over time through something we call a Digital Twin. Your values, your patterns, your emotional readiness, your constraints. Every conversation, every journal entry, every insight builds on the last. Nia coordinates a team of specialized agents working together on your behalf. That is something ChatGPT was never designed to do and cannot do.",
      ],
    },
    {
      q: "What is Nia?",
      a: [
        "Nia is your guide throughout your entire journey on Become. She is the voice you interact with, the intelligence that coordinates everything happening behind the scenes, and the constant through every phase of your transformation.",
        "Behind Nia is a team of specialized agents: an Emotional Readiness Guide, a Career Discovery Coach, a Resource Navigator, and a Transition Specialist. Nia coordinates all of them so you never have to manage multiple tools or repeat yourself. You just have a conversation. Nia handles the rest.",
      ],
    },
    {
      q: "Is this a therapy app?",
      a: [
        "No. Become is a transformation platform, not a clinical service. One of our founders is a Licensed Clinical Social Worker with two decades of practice, and that clinical expertise shapes everything about how Become was designed. But the platform itself does not provide therapy, diagnose, or treat clinical mental health conditions.",
        "What we do is support the emotional and practical work of major life transitions: processing what you're carrying, understanding who you are, exploring where you might go, and managing the actual change. That work is real and it matters, and it is entirely distinct from clinical therapy. If you are in crisis or need clinical support, we will always point you toward the right resources.",
      ],
    },
    {
      q: "Who built this and why?",
      a: [
        "Become was built by two people who kept seeing the same problem from different angles. Adrian, a Licensed Clinical Social Worker with two decades of practice, watched clients who needed transformation support fall through the gap between what traditional therapy offers and what they actually needed to change their lives. John, a Special Operations veteran and cybersecurity engineer, watched the same people get handed generic tools that weren't built for them and didn't actually help.",
        "Become is what happens when you combine clinical expertise, military leadership philosophy, and enterprise-grade security architecture and point all of it at the problem of making real transformation support accessible to people who have been priced out or left behind.",
      ],
    },
    {
      q: "What makes Become different from other career platforms?",
      a: [
        "Most career platforms start with what you've already done. Your resume, your experience, your degree. That's useful, but it's incomplete. It tells you what you've tried, not who you are.",
        "Become starts with you. Not your work history. Not your LinkedIn profile. You. Your values, your patterns, your emotional readiness, your real constraints. Then it maps who you actually are to what actually exists in the world, and supports you through the entire transition, not just the research phase. No other platform does all of that in one place, with one integrated team, guided by clinical frameworks and built on enterprise-grade security.",
      ],
    },
  ],
  process: [
    {
      q: "How does the journey actually work?",
      a: [
        "The journey moves through three phases: Reflect, Discover, and Become. In Reflect, you process what you're carrying and build the emotional clarity that makes everything after it work. In Discover, you explore who you are and where you might go, including paths you may never have considered. In Become, you take action, manage the real transition, and handle the setbacks that come with any meaningful change.",
        "Throughout all three phases, Nia coordinates your full support team so nothing falls through the cracks. You don't manage the process. You just show up.",
      ],
    },
    {
      q: "How long does this take?",
      a: [
        "It takes the time it takes. That is not a non-answer. It is a design principle.",
        "Most platforms give you arbitrary timelines because timelines feel productive. But transformation doesn't work on a calendar. It works on readiness. Become uses what we call conditions-based progression, which means you move forward when the foundation is solid, not when a timer runs out. For most people navigating a career transition, the full journey is somewhere between six and eighteen months. But your journey is yours.",
      ],
    },
    {
      q: "What is conditions-based progression?",
      a: [
        "It means you advance through phases based on where you actually are, not based on how long you've been in the app. Before you move from Reflect to Discover, the platform looks at real indicators: your emotional clarity, the consistency of your thinking, whether the foundation is stable enough to build on. If you're ready early, you move. If you need more time, you stay, with support, without judgment, without pressure. No countdown timers. No \"you should be further along by now.\"",
      ],
    },
    {
      q: "What happens if I get stuck?",
      a: [
        "Getting stuck is part of the process, not a sign that something has gone wrong. Nia is built to recognize when you're stuck and to work with you through it rather than past it. Sometimes stuck means you need more time in the current phase. Sometimes it means something important hasn't been addressed yet. Sometimes it means the direction you've been exploring isn't actually the right one. Nia helps you figure out which one it is.",
      ],
    },
    {
      q: "Can I go at my own pace?",
      a: [
        "Yes, entirely. There are no session minimums, no weekly check-in requirements, no notifications designed to guilt you back into the app. You engage when you're ready. The platform builds on itself regardless of the gaps between sessions because your Digital Twin maintains your complete context. You never have to start over.",
      ],
    },
  ],
  experience: [
    {
      q: "What does a typical session feel like?",
      a: [
        "It depends on where you are in your journey and what you need that day. Some sessions are conversations with Nia, processing something that happened, exploring a new career direction, working through a specific obstacle. Some sessions are journaling, writing into a space that holds everything you share and connects it to the larger picture over time. Some sessions are shorter, a check-in, a reflection, a small step forward. There is no prescribed format. The platform adapts to you.",
      ],
    },
    {
      q: "How does Nia know what I need?",
      a: [
        "Nia draws on your Digital Twin, a continuously updated understanding of who you are built from everything you've shared. Your values, your patterns, your emotional state, your career interests, your constraints, your history on the platform. Nia doesn't start fresh every session. She knows where you've been, what you've said, what's changed, and what still needs attention. The longer you engage, the better the support becomes.",
      ],
    },
    {
      q: "Will this feel like talking to a robot?",
      a: [
        "We've worked hard to make sure it doesn't. Nia is warm, direct, and genuinely curious. She doesn't give scripted responses or generic advice. She responds to what you actually said, in the context of everything she knows about you, guided by clinical frameworks developed by a practicing therapist.",
        "That said, Nia is AI. She will never pretend otherwise. We think honesty about that is part of what makes the experience trustworthy, and we think the genuine value of what Nia offers speaks for itself without needing to pretend she's something she isn't.",
      ],
    },
    {
      q: "What if I'm not a journaler?",
      a: [
        "You don't have to be. Journaling is one way to engage with the platform but it isn't the only way. Conversation with Nia is equally valid and equally effective. Some people find that talking through things in conversation unlocks more than writing does. The platform is built to work either way, and Nia draws on everything regardless of the format it came in.",
      ],
    },
    {
      q: "What happens when my situation changes mid-journey?",
      a: [
        "Life doesn't pause while you're doing transformation work, and Become isn't designed as if it does. If your circumstances shift, if a new opportunity appears, if a constraint changes, if your direction evolves, Nia adapts. Your Digital Twin updates as you share new information. The support team recalibrates. The journey continues, just in the direction that's actually relevant now.",
      ],
    },
  ],
  privacy: [
    {
      q: "Who can see what I share?",
      a: [
        "Your content is yours and only yours. No human at Become reviews your journal entries or your conversations with Nia. The system is designed so that the people who built it cannot access what you share. This isn't a policy promise. It's an architectural reality. We use Zero Trust Architecture, the same privacy standard used in defense and financial services, which means your identity and your transformation data are cryptographically separated at the infrastructure level.",
      ],
    },
    {
      q: "Is my data sold or used to train AI?",
      a: [
        "Never. We do not sell your data. We do not sell insights derived from your data. We do not train AI models on your content without your explicit consent. Most apps treat your data as a product. We treat it as something that belongs to you.",
      ],
    },
    {
      q: "What happens to my data if I leave?",
      a: [
        "It leaves with you or it disappears, your choice. You can download everything in standard formats at any time. If you want to delete your account, everything goes: your journal entries, your Digital Twin, your conversations, your assessment results. Permanently. We do not keep it for ninety days. We do not archive it for legal purposes. When you say delete, we delete.",
      ],
    },
    {
      q: "How is this different from other apps that claim to protect my privacy?",
      a: [
        "Most apps protect your privacy through policy. We protect it through architecture. There is a meaningful difference. A policy can be changed. An architecture cannot be quietly overridden. Because your identity and your content are cryptographically separated at the infrastructure level, even a breach of our systems cannot expose both simultaneously. An attacker gets either a list of email addresses with no associated content, or anonymous journal entries with no way to identify the author. Never both. That is Zero Trust Architecture, and it is rare in consumer products.",
      ],
    },
    {
      q: "Is this HIPAA compliant?",
      a: [
        "We are not legally required to be HIPAA compliant because we are not a covered healthcare entity. We are not a clinical service. However, we have built the platform to align with HIPAA standards because we believe the sensitivity of what people share with us deserves that level of protection regardless of legal requirement. We conduct regular third-party security audits, maintain breach notification protocols, and hold ourselves to a standard that most consumer apps never consider.",
      ],
    },
  ],
  outcomes: [
    {
      q: "How do I know if this is working?",
      a: [
        "Progress on Become isn't measured by how often you open the app or how many sessions you've completed. It's measured by clarity. Can you describe your situation more clearly than you could before? Are you making decisions from a more grounded place? Do you understand yourself better? Are you moving toward something real?",
        "The platform tracks your emotional readiness and cognitive coherence over time and reflects that back to you. But the most honest measure is simpler: does your life feel more like it fits?",
      ],
    },
    {
      q: "What does success actually look like?",
      a: [
        "Success looks like arriving somewhere you chose, with confidence that you chose it for the right reasons. Not just landing a new job, but landing the right one for who you actually are. Not just making a change, but making a sustainable one you won't regret in two years. We measure outcomes not just at the moment of transition but beyond it, because a successful transformation holds.",
      ],
    },
    {
      q: "What happens when I finish?",
      a: [
        "You graduate. That is the word we use intentionally. You came, you did the work, you transformed, and now you move on. There are no streak counters trying to pull you back. No notifications reminding you that you haven't checked in. No subscription model designed to make leaving feel like loss. We celebrate when you finish because your finishing is the whole point.",
      ],
    },
    {
      q: "What if I don't reach my goal?",
      a: [
        "Goals change, and that's not failure. Sometimes what you discover in the process is that the goal you started with wasn't actually the right one. Become is designed to help you find the direction that's genuinely right for you, not to march you toward a predetermined destination regardless of what you learn along the way. If your direction shifts, the platform shifts with you.",
      ],
    },
    {
      q: "Will I become dependent on this?",
      a: [
        "That is something we actively work against. Every design decision we make is oriented toward building your capacity to navigate your own life, not toward making you need us to do it. Nia reinforces your agency. The platform attributes your progress to you, not to itself. We measure success by your independence, and we consider dependency a failure of our design, not a feature of our business model.",
      ],
    },
  ],
};

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState("product");

  useEffect(() => {
    const handleScroll = () => {
      for (const cat of categories) {
        const el = document.getElementById(`faq-${cat.id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 180 && rect.bottom > 180) {
            setActiveCategory(cat.id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToCategory = (id) => {
    setActiveCategory(id);
    const el = document.getElementById(`faq-${id}`);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[45vh] flex items-center justify-center hero-dark" data-testid="faq-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-16 right-20 w-72 h-72 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-16 w-64 h-64 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2 mb-8"
          >
            <HelpCircle className="w-4 h-4 text-brand-peach" />
            <span className="text-brand-peach/90 text-sm font-medium tracking-wide">Your questions, answered</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            data-testid="faq-title"
          >
            Frequently Asked <span className="gradient-text">Questions</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-2xl mx-auto"
          >
            Everything you need to know about Become, how it works, and what makes it different.
          </motion.p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-warm-bone" data-testid="faq-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Sidebar (desktop) */}
            <aside className="hidden lg:block lg:w-64 shrink-0">
              <div className="sticky top-32">
                <p className="text-text-dark/40 text-xs font-medium uppercase tracking-widest mb-5">Categories</p>
                <nav className="space-y-1" data-testid="faq-sidebar-nav">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => scrollToCategory(cat.id)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        activeCategory === cat.id
                          ? "bg-white text-deep-violet shadow-sm"
                          : "text-text-dark/60 hover:text-text-dark hover:bg-white/50"
                      }`}
                      data-testid={`faq-nav-${cat.id}`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 max-w-3xl">
              {categories.map((cat, catIdx) => (
                <motion.div
                  key={cat.id}
                  id={`faq-${cat.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: catIdx * 0.05 }}
                  className={catIdx > 0 ? "mt-16" : ""}
                  data-testid={`faq-section-${cat.id}`}
                >
                  {/* Category Header */}
                  <div className="mb-6">
                    <h2 className="font-heading text-2xl sm:text-3xl text-text-dark">{cat.label}</h2>
                    <div className="mt-2 h-0.5 w-12 bg-gradient-to-r from-brand-peach to-brand-lavender rounded-full" />
                  </div>

                  {/* Questions */}
                  <Accordion type="single" collapsible className="space-y-3">
                    {faqData[cat.id].map((item, idx) => (
                      <AccordionItem
                        key={idx}
                        value={`${cat.id}-${idx}`}
                        className="bg-white rounded-2xl border border-gray-100 px-6 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                        data-testid={`faq-item-${cat.id}-${idx}`}
                      >
                        <AccordionTrigger
                          className="text-base sm:text-lg font-medium text-text-dark py-5 hover:no-underline"
                          data-testid={`faq-trigger-${cat.id}-${idx}`}
                        >
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent className="pb-6">
                          <div className="space-y-4 max-w-2xl">
                            {item.a.map((para, pIdx) => (
                              <p key={pIdx} className="text-text-dark/70 text-[15px] leading-relaxed">
                                {para}
                              </p>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
